import React, { memo, useEffect, useMemo } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { ApiToken } from '../../api/types';
import type {
  Account, GlobalState, SavedAddress, UserToken,
} from '../../global/types';

import {
  ANIMATED_STICKER_SMALL_SIZE_PX,
  BURN_ADDRESS,
  BURN_CHUNK_DURATION_APPROX_SEC,
  IS_LEGENDS_WALLET,
  NFT_BATCH_SIZE,
  NOTCOIN_EXCHANGERS,
  STARS_SYMBOL,
} from '../../config';
import renderText from '../../global/helpers/renderText';
import { selectCurrentAccountId, selectNetworkAccounts } from '../../global/selectors';
import buildClassName from '../../util/buildClassName';
import { getChainConfig, getChainTitle } from '../../util/chain';
import { toDecimal } from '../../util/decimals';
import { getLocalAddressName } from '../../util/getLocalAddressName';
import { vibrate } from '../../util/haptics';
import { getChainBySlug } from '../../util/tokens';
import { getTransferQuoteRefreshDelay } from '../../util/transferQuote';
import { ANIMATED_STICKERS_PATHS } from '../ui/helpers/animatedAssets';

import useHistoryBack from '../../hooks/useHistoryBack';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import WalletSponsorshipFee from '../common/WalletSponsorshipFee';
import AmountWithFeeTextField from '../ui/AmountWithFeeTextField';
import AnimatedIconWithPreview from '../ui/AnimatedIconWithPreview';
import Button from '../ui/Button';
import Fee from '../ui/Fee';
import IconWithTooltip from '../ui/IconWithTooltip';
import InteractiveTextField from '../ui/InteractiveTextField';
import ModalHeader from '../ui/ModalHeader';
import NftChips from './NftChips';
import NftInfo from './NftInfo';

import modalStyles from '../ui/Modal.module.scss';
import styles from './Transfer.module.scss';

interface OwnProps {
  isActive: boolean;
  savedAddresses?: SavedAddress[];
  token?: UserToken | ApiToken;
  onBack: NoneToVoidFunction;
  onClose: NoneToVoidFunction;
}

interface StateProps {
  currentAccountId: string;
  currentTransfer: GlobalState['currentTransfer'];
  accounts?: Record<string, Account>;
}

function TransferConfirm({
  currentTransfer: {
    tokenSlug,
    amount,
    toAddress,
    resolvedAddress,
    comment,
    shouldEncrypt,
    promiseId,
    isLoading,
    toAddressName,
    isToNewAddress,
    isScam,
    binPayload,
    nfts,
    isGaslessWithStars,
    diesel,
    stateInit,
    isOfframp,
    isNftBurn,
    explainedFee,
    isQuoteReady,
    sponsorship,
  },
  token,
  currentAccountId,
  accounts,
  isActive,
  savedAddresses,
  onBack,
  onClose,
}: OwnProps & StateProps) {
  const { submitTransferConfirm, submitTransferInitial } = getActions();

  const lang = useLang();

  const isNftTransfer = Boolean(nfts?.length);
  const isQuotePending = IS_LEGENDS_WALLET && !isOfframp && !isQuoteReady;
  if (isNftTransfer) {
    const nftChain = nfts[0].chain;
    tokenSlug = getChainConfig(nftChain).nativeToken.slug;
  }

  const chain = getChainBySlug(tokenSlug);
  const localAddressName = useMemo(() => getLocalAddressName({
    address: toAddress!,
    chain,
    currentAccountId,
    accounts: accounts!,
    savedAddresses,
  }), [accounts, chain, currentAccountId, savedAddresses, toAddress]);
  const addressName = localAddressName || toAddressName;
  const isBurning = resolvedAddress === BURN_ADDRESS || isNftBurn;
  const isNotcoinBurning = resolvedAddress === NOTCOIN_EXCHANGERS[0];

  useHistoryBack({
    isActive,
    onBack,
  });

  useEffect(() => {
    if (!IS_LEGENDS_WALLET || !isActive || isOfframp || isNftTransfer || isLoading || !isQuoteReady
      || !sponsorship?.expiresAt || !tokenSlug || !toAddress || !amount) return undefined;

    const timer = window.setTimeout(() => {
      submitTransferInitial({ tokenSlug, toAddress, amount, comment, shouldEncrypt, binPayload, stateInit });
    }, getTransferQuoteRefreshDelay(sponsorship.expiresAt));
    return () => window.clearTimeout(timer);
  }, [amount, binPayload, comment, isActive, isLoading, isNftTransfer, isOfframp, isQuoteReady,
    shouldEncrypt, sponsorship?.expiresAt, stateInit, submitTransferInitial, toAddress, tokenSlug]);

  const handleConfirm = useLastCallback(() => {
    vibrate();
    submitTransferConfirm();
  });

  function renderNfts() {
    if (nfts!.length === 1) {
      return <NftInfo nft={nfts![0]} withMediaViewer />;
    }

    return <NftChips nfts={nfts!} />;
  }

  function renderAmountWithFee() {
    if ((!explainedFee?.realFee && !isQuotePending) || !token) {
      return undefined;
    }

    const feeText = explainedFee?.realFee ? (
      <Fee
        terms={explainedFee.realFee.terms}
        precision={explainedFee.realFee.precision}
        token={token}
        symbolClassName={styles.currencySymbol}
      />
    ) : lang('Loading...');

    return isNftTransfer ? (
      <>
        <div className={styles.label}>{lang('Fee')}</div>
        <div className={styles.inputReadOnly}>{feeText}</div>
      </>
    ) : (
      <AmountWithFeeTextField
        label={lang('Amount')}
        amount={toDecimal(amount ?? 0n, token?.decimals)}
        symbol={token?.symbol ?? ''}
        feeText={sponsorship ? undefined : feeText}
        fractionDigits={token?.decimals}
      />
    );
  }

  function renderComment() {
    if (binPayload || stateInit) {
      return (
        <>
          {binPayload && (
            <>
              <div className={styles.label}>{lang('Signing Data')}</div>
              <InteractiveTextField
                text={binPayload}
                copyNotification={lang('Data Copied')}
                className={styles.addressWidget}
              />
            </>
          )}

          {stateInit && (
            <>
              <div className={styles.label}>{lang('Contract Initialization Data')}</div>
              <InteractiveTextField
                text={stateInit}
                copyNotification={lang('Data Copied')}
                className={styles.addressWidget}
              />
            </>
          )}

          <div className={styles.error}>
            {renderText(lang('$signature_warning'))}
          </div>
        </>
      );
    }

    if (!comment) {
      return undefined;
    }

    return (
      <>
        <div className={styles.label}>{shouldEncrypt ? lang('Encrypted Message') : lang('Comment or Memo')}</div>
        <div className={buildClassName(styles.inputReadOnly, styles.inputReadOnly_words, styles.commentInputWrapper)}>
          {comment}
        </div>
      </>
    );
  }

  function renderWalletSponsorshipComparison() {
    if (!sponsorship || !token) {
      return undefined;
    }

    return (
      <WalletSponsorshipFee
        serviceFee={sponsorship.serviceFee}
        token={token}
      />
    );
  }

  function getBurningDurationText(nftsCount: number) {
    const durationSeconds = Math.ceil(nftsCount / NFT_BATCH_SIZE) * BURN_CHUNK_DURATION_APPROX_SEC;

    return lang('$duration_minutes', Math.ceil(durationSeconds / 60));
  }

  function getSubmitBtnText() {
    if (isOfframp) {
      return lang('Sell %symbol%', { symbol: token?.symbol ?? '' });
    }
    if (isBurning || isNotcoinBurning) {
      return lang(isNftTransfer ? ((nfts?.length ?? 0) > 1 ? 'Burn Collectibles' : 'Burn NFT') : 'Burn');
    }
    if (isGaslessWithStars) {
      return lang('Pay fee with %stars_symbol%', { stars_symbol: STARS_SYMBOL });
    }
    return lang('Confirm');
  }

  return (
    <>
      <ModalHeader title={lang('Is it all ok?')} onClose={onClose} />
      <div className={modalStyles.transitionContent}>
        {isNftTransfer ? renderNfts() : (
          <AnimatedIconWithPreview
            size={ANIMATED_STICKER_SMALL_SIZE_PX}
            play={isActive}
            noLoop={false}
            nonInteractive
            className={buildClassName(styles.sticker, styles.sticker_sizeSmall)}
            tgsUrl={ANIMATED_STICKERS_PATHS.bill}
            previewUrl={ANIMATED_STICKERS_PATHS.billPreview}
          />
        )}
        {!isNftBurn && (
          <>
            <div className={styles.label}>
              {lang('Receiving Address')}
              {' '}
              {isToNewAddress && (
                <IconWithTooltip
                  emoji="⚠️"
                  size="small"
                  message={lang('This address is new and never received transfers before.')}
                  tooltipClassName={styles.warningTooltipContainer}
                />
              )}
            </div>
            <InteractiveTextField
              chain={chain}
              address={resolvedAddress}
              addressName={addressName}
              isScam={isScam}
              copyNotification={lang('%chain% Address Copied', { chain: getChainTitle(chain) }) as string}
              className={styles.addressWidget}
              forceFullAddress
            />
          </>
        )}
        {renderAmountWithFee()}
        {renderWalletSponsorshipComparison()}
        {renderComment()}

        {nfts && (isBurning || (isNotcoinBurning && nfts?.length > 1)) && (
          <div className={styles.burnWarning}>
            {(
              nfts?.length === 1 ? (
                renderText(lang('Are you sure you want to burn this NFT? It will be lost forever.'))
              ) : renderText(
                (lang('$multi_burn_nft_warning'))
                  .replace('%amount%', String(nfts.length))
                  .replace('%duration%', getBurningDurationText(nfts.length) as string),
              )
            )}
          </div>
        )}

        <div className={buildClassName(modalStyles.buttons, modalStyles.buttonsInsideContentWithScroll)}>
          {!isOfframp && (
            <Button className={modalStyles.button} onClick={promiseId ? onClose : onBack}>
              {promiseId ? lang('Cancel') : lang('Edit')}
            </Button>
          )}
          <Button
            isPrimary
            isLoading={isLoading}
            isDisabled={isQuotePending}
            isDestructive={isBurning || isScam}
            className={modalStyles.button}
            onClick={handleConfirm}
          >
            {getSubmitBtnText()}
          </Button>
        </div>
      </div>
    </>
  );
}

export default memo(withGlobal<OwnProps>((global): StateProps => {
  return {
    currentAccountId: selectCurrentAccountId(global)!,
    currentTransfer: global.currentTransfer,
    accounts: selectNetworkAccounts(global),
  };
})(TransferConfirm));
